import { currencyGet, currencyPost, expectItems, type ApiEnvelope } from '@/lib/task-api';
import type { ServerPage, ServerListPageArgs } from '@/lib/task-server-list';
import { toTaskRow, type TaskRow } from './types';

/**
 * TALEP UCLARI.
 *
 * `/v1/tasks`        : listeleme (sunucu tarafli sayfalama/filtre/siralama)
 * `/v1/tasks`  POST  : talep olusturma
 * `/v1/tasks/update` : kazanan secme, iptal, "odeme yapilmadi" uyarisi
 *
 * "Guncelleme" ucunun POST /v1/tasks/update olmasi (PATCH degil) sunucu
 * sozlesmesi; degistirilemez.
 */

type ListResponse = ApiEnvelope & {
  items?: unknown;
  page?: ServerPage<TaskRow>['page'];
};

/**
 * Sekme rozeti icin TOPLAM kayit sayisi.
 *
 * Neden ayri bir uc: liste yaniti `total` TASIMIYOR (bkz. lib/task-server-list.ts) --
 * `limit+1` sentinel'i yalnizca `hasMore` uretiyor. Sunucu tarafinda bu uc, liste ile AYNI
 * sorgudan (countOnly bayragi) besleniyor, ayri bir sorgu degil; boylece "sayac = listedeki
 * kayit sayisi" ayrisamiyor.
 */
export const fetchTaskCount = async (
  params: Record<string, string | number | undefined>,
): Promise<number> => {
  const { data } = await currencyGet<ApiEnvelope & { total?: number }>('/v1/tasks/counts', params);
  return typeof data.total === 'number' ? data.total : 0;
};

export const fetchTaskPage = async ({
  params,
  limit,
  offset,
  snapshotAt,
}: ServerListPageArgs): Promise<ServerPage<TaskRow>> => {
  const { data, receivedAtMs } = await currencyGet<ListResponse>('/v1/tasks', {
    ...params,
    limit,
    offset,
    // Ilk sayfada damga YOK — sunucu yenisini uretir ve yanitinda geri verir.
    ...(snapshotAt === null ? {} : { snapshotAt }),
  });
  return {
    items: expectItems(data, '/v1/tasks').map(toTaskRow),
    page: data.page ?? {},
    receivedAtMs,
  };
};

/**
 * Tek talep. Derin baglanti / yenileme icin; liste onbellegi bos oldugunda.
 *
 * Filtre `ids`, `taskId` DEGIL — canli sunucuda dogrulandi: `taskId` diye bir parametre
 * yok, gonderilirse SESSIZCE YOK SAYILIR ve uc normal listeyi doner (ilk deneme tam olarak
 * bunu yapiyordu; asagidaki kimlik kontrolu sayesinde yanlis talep gosterilmiyordu ama
 * derin baglanti hep "bulunamadi" veriyordu). `ids` virgulle coklu kabul eder.
 *
 * SAHIPLIK PARAMETRESI ZORUNLU: app-service, ucunden hicbir sahiplik alani gelmezse en dar
 * okumaya duser. Musteri `taskUserId`, buro `taskPretenderCurrencyUserId` gonderir; degeri
 * sunucu yine de dogrulanmis cagirana ZORLAR, yani baskasinin id'sini yazmak ise yaramaz
 * (canli olarak denendi: baska kullanicinin talebi BOS liste doner).
 */
export const fetchTaskById = async (
  taskId: string,
  ownership: Record<string, string>,
): Promise<TaskRow | null> => {
  const { data } = await currencyGet<ListResponse>('/v1/tasks', {
    ...ownership,
    ids: taskId,
    limit: 1,
    offset: 0,
  });
  const first = expectItems(data, '/v1/tasks')[0];
  if (!first) return null;
  const row = toTaskRow(first);
  // Sunucu filtreyi yok saymis olabilir; yanlis gorevi gostermektense "bulunamadi" demek
  // dogru. Kimlik karsilastirmasi STRING uzerinden (pg int8 -> string).
  return String(row.id) === String(taskId) ? row : null;
};

export type CreateTaskInput = {
  taskUserId: string;
  taskType: string;
  taskAmount: number;
  taskIsBuying: boolean;
  taskData: Record<string, unknown>;
};

export const createTask = (input: CreateTaskInput): Promise<ApiEnvelope> =>
  currencyPost<ApiEnvelope>('/v1/tasks', { ...input });

export type UpdateTaskInput = {
  taskId: string;
  taskStatus: string;
  /** Kazanan buronun kimligi — yalniz `taskStatus: 'pending'` ile birlikte. */
  taskPretenderUserId?: string;
};

export const updateTask = (input: UpdateTaskInput): Promise<ApiEnvelope> =>
  currencyPost<ApiEnvelope>('/v1/tasks/update', { ...input });
